import React from 'react';
import { Mail } from 'lucide-react';
import { Logo } from '@/components/brand/Logo';

const columns = [
  {
    title: 'Product',
    links: [
      { label: 'How it works', href: '/#how-it-works' },
      { label: 'Pricing', href: '/pricing' },
    ],
  },
  {
    title: 'Legal',
    links: [
      { label: 'Privacy Policy', href: '/privacy' },
      { label: 'Terms of Service', href: '/terms' },
    ],
  },
];

/**
 * Marketing ruling M1 (2026-10-20): renders nothing until counsel writes
 * the regulatory status line (M1(5)(h)). Set the string here when it exists.
 */
export const REGULATORY_STATUS_LINE: string | null = null;

/**
 * Social links (twitter/linkedin/github) were removed under M1 delete list
 * item 56: the accounts could not be verified to exist, and a dead link
 * costs trust. Re-add individually once each account is confirmed.
 */
export const Footer: React.FC = () => {
  return (
    <footer className="pt-20 pb-10 px-6 border-t border-white/[0.04]">
      <div className="max-w-[1200px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-[2fr_repeat(2,1fr)] gap-12 mb-16">
          <div>
            <Logo variant="full" iconSize={28} />
            <p className="text-white/40 leading-relaxed max-w-[300px] mt-4 text-sm">
              Documented rules, run inside limits you sign, on the record.
            </p>
            <a
              href="mailto:support@mendllabs.ai"
              className="inline-flex items-center gap-2 text-white/40 hover:text-white/70 text-sm mt-4 transition-colors"
            >
              <Mail size={14} />
              support@mendllabs.ai
            </a>
            <p className="text-white/25 text-xs mt-6">Every order is checked against your limits before it is sent.</p>
          </div>
          {columns.map((col) => (
            <div key={col.title}>
              <h4 className="text-white/80 text-sm font-semibold mb-5 uppercase tracking-wider">
                {col.title}
              </h4>
              <ul className="space-y-1">
                {col.links.map((link) => (
                  <li key={link.label}>
                    <a
                      href={link.href}
                      className="inline-flex min-h-[44px] items-center text-white/40 text-sm hover:text-white/70 transition-colors cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2dd4a0] rounded-sm"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {REGULATORY_STATUS_LINE && (
          <p data-testid="regulatory-status" className="text-white/30 text-xs mb-6 max-w-[760px]">
            {REGULATORY_STATUS_LINE}
          </p>
        )}

        <div className="border-t border-white/[0.04] pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/20 text-sm">
            &copy; {new Date().getFullYear()} Mendl Labs. All rights reserved.
          </p>
          <div className="flex gap-6">
            <a href="/privacy" className="inline-flex min-h-[44px] items-center text-white/20 text-sm hover:text-white/40 transition-colors">
              Privacy Policy
            </a>
            <a href="/terms" className="inline-flex min-h-[44px] items-center text-white/20 text-sm hover:text-white/40 transition-colors">
              Terms of Service
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};
